import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Table, Form, Dropdown, InputGroup } from "react-bootstrap";
import useFetch from "../../hooks/fetchMSSQL-hook";
import moment from "moment";

const EMPTY_ERR = Object.freeze({});
const STORAGE_KEY = "refundList";
const AMOUNT_RX = /^\d*(?:\.\d{0,2})?$/;

const safeFixed2 = (v, fallback = 0) => {
  const raw = String(v ?? "").trim().replace(",", ".");
  const n = Number.parseFloat(raw);
  const use = Number.isFinite(n) ? n : Number(fallback);
  return Number.isFinite(use) ? use.toFixed(2) : "";
};

const formatAmount2 = (s) => {
  const raw = String(s ?? "").trim().replace(",", ".");
  if (!raw) return "";
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const RefundRow = memo(function RefundRow({
  r,
  e,
  onCommitRow,
  onRemove,
  formatAccountLive,
  getAccountLiveError,
}) {
  const err = e || EMPTY_ERR;

  const [refundAmount, setRefundAmount] = useState(() =>
    safeFixed2(r.RefundAmount, Number.parseFloat(r.Price ?? 0) || 0)
  );
  const [creditorAccount, setCreditorAccount] = useState(r.CreditorAccount ?? "");
  const [title, setTitle] = useState(r.Title ?? "");

  const [touchedCreditorAcc, setTouchedCreditorAcc] = useState(false);

  const creditorAccRef = useRef(null);
  const pendingCreditorCaret = useRef(null);

  useEffect(() => {
    setRefundAmount(safeFixed2(r.RefundAmount, Number.parseFloat(r.Price ?? 0) || 0));
  }, [r.TrxId, r.RefundAmount, r.Price]);

  useEffect(() => setCreditorAccount(r.CreditorAccount ?? ""), [r.TrxId, r.CreditorAccount]);
  useEffect(() => setTitle(r.Title ?? ""), [r.TrxId, r.Title]);

  const commit = (patch) => onCommitRow(r.TrxId, patch);

  const isAlnum = (ch) => {
    const c = ch.charCodeAt(0);
    return (c >= 48 && c <= 57) || (c >= 65 && c <= 90);
  };

  const countAlnumBefore = (s, end) => {
    let n = 0;
    const lim = Math.min(end || 0, s.length);
    for (let i = 0; i < lim; i++) if (isAlnum(s[i])) n++;
    return n;
  };

  const caretFromAlnumCount = (s, n) => {
    if (!n) return 0;
    let seen = 0;
    for (let i = 0; i < s.length; i++) {
      if (isAlnum(s[i])) seen++;
      if (seen >= n) return i + 1;
    }
    return s.length;
  };

  const handleAccountChange = (raw, caretPos) => {
    const upper = String(raw ?? "").toUpperCase();
    const alnumCount = countAlnumBefore(upper, caretPos ?? upper.length);
    const formatted = formatAccountLive(upper);
    const newCaret = caretFromAlnumCount(formatted, alnumCount);

    setTouchedCreditorAcc(true);
    pendingCreditorCaret.current = newCaret;
    setCreditorAccount(formatted);
  };

  useLayoutEffect(() => {
    if (pendingCreditorCaret.current != null && creditorAccRef.current) {
      const pos = pendingCreditorCaret.current;
      pendingCreditorCaret.current = null;
      try {
        creditorAccRef.current.setSelectionRange(pos, pos);
      } catch {}
    }
  }, [creditorAccount]);

  const creditorLiveErr = useMemo(() => {
    if (err.CreditorAccount) return err.CreditorAccount;
    if (!touchedCreditorAcc) return "";
    return getAccountLiveError(creditorAccount);
  }, [creditorAccount, err.CreditorAccount, getAccountLiveError, touchedCreditorAcc]);

  return (
    <tr>
      <td>
        <div style={{ fontSize: 12 }}>
          <div className="text-muted">
            Automat: {r?.MachineFullName}
            <br />
            Terminal: {r?.TerminalTypeName} ({r?.SerialNo})
            <br />
            Numer wydania: {r?.ProductNo}
            <br />
            Kwota: {Number(r?.Price || 0).toFixed(2)}
            <br />
            Data: {r?.CreateDateTime}
            <br />
            Nr karty: {r?.CreditCardNo}
          </div>
        </div>
      </td>

      <td className="text-center p-1" style={{ fontVariantNumeric: "tabular-nums" }}>
        <InputGroup size="sm">
          <Form.Control
            type="text"
            inputMode="decimal"
            value={refundAmount}
            isInvalid={!!err.RefundAmount}
            onChange={(e) => {
              let next = e.target.value.replace(/,/g, ".");
              if (next === "" || AMOUNT_RX.test(next)) setRefundAmount(next);
            }}
            onBlur={() => {
              const fixed = formatAmount2(refundAmount); // zawsze "6.00"
              setRefundAmount(fixed);
              commit({ RefundAmount: fixed });
            }}
            style={{ textAlign: "right" }}
          />
          <InputGroup.Text className="bg-transparent border-0 px-2 text-muted">
            <small>zł</small>
          </InputGroup.Text>
        </InputGroup>
        <Form.Control.Feedback type="invalid">{err.RefundAmount}</Form.Control.Feedback>
      </td>

      <td className="p-1">
        <Form.Control
          ref={creditorAccRef}
          size="sm"
          placeholder="np. 12 3456 ... lub PL..."
          value={creditorAccount}
          isInvalid={!!creditorLiveErr}
          onFocus={() => setTouchedCreditorAcc(true)}
          onChange={(ev) => handleAccountChange(ev.target.value, ev.target.selectionStart)}
          onBlur={() => commit({ CreditorAccount: creditorAccount })}
        />
        <Form.Control.Feedback type="invalid">{creditorLiveErr}</Form.Control.Feedback>
      </td>

      <td className="p-1">
        <Form.Control
          as="textarea"
          rows={3}
          size="sm"
          value={title}
          isInvalid={!!err.Title}
          onChange={(ev) => setTitle(ev.target.value)}
          onBlur={() => commit({ Title: title })}
        />
        <Form.Control.Feedback type="invalid">{err.Title}</Form.Control.Feedback>
      </td>

      <td className="text-center p-1">
        <Button
          variant="link"
          className="p-0 text-danger"
          title="Usuń pozycję z paczki"
          onClick={() => onRemove(r.TrxId)}
        >
          <i className="fas fa-trash" />
        </Button>
      </td>
    </tr>
  );
});

export default function RefundForm({ list = [], setList }) {
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const { fetchMssqlApi } = useFetch();

  const listRef = useRef(list || []);
  useEffect(() => {
    listRef.current = list || [];
  }, [list]);

  const saveTimerRef = useRef(null);

  const saveNow = useCallback((rows) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rows || []));
    } catch {}
  }, []);

  const scheduleSave = useCallback(
    (rows) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveNow(rows);
        saveTimerRef.current = null;
      }, 150);
    },
    [saveNow]
  );

  const flushSave = useCallback(
    (rows) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      saveNow(rows);
    },
    [saveNow]
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // ===== account helpers =====
  const normalizeAccount = useCallback(
    (s) => (s || "").toString().trim().replace(/\s+/g, "").toUpperCase(),
    []
  );

  const digitsOnly = useCallback((s) => (s || "").toString().replace(/\D/g, ""), []);

  const ibanToDigits = useCallback((iban) => {
    const moved = iban.slice(4) + iban.slice(0, 4);
    let out = "";
    for (const ch of moved) {
      if (ch >= "0" && ch <= "9") out += ch;
      else if (ch >= "A" && ch <= "Z") out += (ch.charCodeAt(0) - 55).toString();
      else return null;
    }
    return out;
  }, []);

  const mod97 = useCallback((digits) => {
    let rem = 0;
    for (let i = 0; i < digits.length; i++) {
      const c = digits[i];
      if (c < "0" || c > "9") return NaN;
      rem = (rem * 10 + (c.charCodeAt(0) - 48)) % 97;
    }
    return rem;
  }, []);

  const isValidPLAccount = useCallback(
    (value) => {
      const acc = normalizeAccount(value);
      if (!acc) return false;

      if (/^\d{26}$/.test(acc)) {
        const digits = ibanToDigits("PL" + acc);
        return !!digits && mod97(digits) === 1;
      }

      if (/^PL\d{26}$/.test(acc)) {
        const digits = ibanToDigits(acc);
        return !!digits && mod97(digits) === 1;
      }

      return false;
    },
    [ibanToDigits, mod97, normalizeAccount]
  );

  const getAccountLiveError = useCallback(
    (value) => {
      const acc = normalizeAccount(value);
      if (!acc) return "Wymagane";

      const d = acc.startsWith("PL") ? digitsOnly(acc.slice(2)) : digitsOnly(acc);
      if (d.length < 26) return "Wpisz 26 cyfr";
      if (d.length > 26) return "Za dużo cyfr";
      if (!isValidPLAccount(acc)) return "Niepoprawny numer konta";
      return "";
    },
    [digitsOnly, isValidPLAccount, normalizeAccount]
  );

  const toNRBDigits26 = useCallback(
    (value) => {
      const acc = normalizeAccount(value);
      if (!acc) return "";
      if (acc.startsWith("PL")) return digitsOnly(acc.slice(2)).slice(0, 26);
      return digitsOnly(acc).slice(0, 26);
    },
    [digitsOnly, normalizeAccount]
  );

  const formatAccountLive = useCallback((input) => {
    const s = String(input ?? "").toUpperCase();
    const first = (s.match(/[A-Z0-9]/) || [""])[0];
    const raw = s.replace(/[^A-Z0-9]/g, "");
    const group4 = (t) => (t.match(/.{1,4}/g)?.join(" ") ?? "");

    if (first >= "0" && first <= "9") {
      const d = raw.replace(/\D/g, "").slice(0, 26);
      const head = d.slice(0, 2);
      const rest = d.slice(2);
      return (head + (rest ? " " + group4(rest) : "")).trim();
    }

    const letters = raw.replace(/[^A-Z]/g, "");
    const country = letters.slice(0, 2);
    const digits = raw.slice(country.length).replace(/\D/g, "").slice(0, 26);
    return group4((country + digits).slice(0, 28));
  }, []);

  // ===== enrich on open (only missing fields) =====
  const defaultTitle = useCallback(
    (trx) => `Zwrot środków, niewydany produkt / usługa dnia: ${trx?.CreateDateTime ?? ""}`,
    []
  );

  const enrichIfMissing = useCallback(
    (trx) => {
      const t = trx || {};
      const price = Number.parseFloat(t.Price ?? 0) || 0;

      const patch = {};

      const targetRefund = safeFixed2(t.RefundAmount ?? price, price);
      if (t.RefundAmount == null || String(t.RefundAmount) !== targetRefund) patch.RefundAmount = targetRefund;

      if (t.CreditorAccount == null) patch.CreditorAccount = "";
      if (t.Title == null) patch.Title = defaultTitle(t);

      return Object.keys(patch).length ? { ...t, ...patch } : t;
    },
    [defaultTitle]
  );

  const getId = useCallback((t) => String(t?.TrxId ?? ""), []);

  useEffect(() => {
    if (!show) return;

    const current = Array.isArray(list) ? list : [];
    const enriched = current.map(enrichIfMissing);
    const changed = enriched.some((x, i) => x !== current[i]);

    if (changed) {
      listRef.current = enriched;
      setList(enriched);
      scheduleSave(enriched);
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const setRow = useCallback(
    (trxId, patch) => {
      const id = String(trxId);
      const prev = listRef.current || [];
      const idx = prev.findIndex((x) => getId(x) === id);
      if (idx < 0) return;

      const nextRow = { ...prev[idx], ...patch };
      const next = prev.slice();
      next[idx] = nextRow;

      listRef.current = next;
      setList(next);
      scheduleSave(next);

      setErrors((prevErr) => ({
        ...prevErr,
        [id]: {
          ...(prevErr[id] || {}),
          ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])),
        },
      }));
    },
    [getId, scheduleSave, setList]
  );

  const removeOne = useCallback(
    (trxId) => {
      const id = String(trxId);
      const prev = listRef.current || [];
      const next = prev.filter((x) => getId(x) !== id);

      listRef.current = next;
      setList(next);
      flushSave(next);

      setErrors((prevErr) => {
        const n = { ...prevErr };
        delete n[id];
        return n;
      });
    },
    [flushSave, getId, setList]
  );

  const forceBlurActive = useCallback(() => {
    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}
  }, []);

  const onClose = useCallback(() => {
    forceBlurActive();
    flushSave(listRef.current || []);
    setShow(false);
  }, [flushSave, forceBlurActive]);

  const clearAll = useCallback(() => {
    listRef.current = [];
    setList([]);
    setErrors({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setShow(false);
  }, [setList]);

  const validateAll = useCallback(() => {
    const rows = listRef.current || [];
    const nextErrors = {};

    for (const r of rows) {
      const e = {};

      const amt = Number.parseFloat(String(r.RefundAmount ?? "").replace(",", "."));

      if (!(amt > 0)) e.RefundAmount = "Kwota musi być > 0";

      if (!r.CreditorAccount) e.CreditorAccount = "Wymagane";
      if (r.CreditorAccount && !isValidPLAccount(normalizeAccount(r.CreditorAccount)))
        e.CreditorAccount = "Niepoprawny numer konta";

      const creditor26 = toNRBDigits26(r.CreditorAccount);
      if (creditor26.length !== 26) e.CreditorAccount = "Wymagany poprawny numer konta";

      if (!String(r.Title || "").trim()) e.Title = "Wymagane";

      if (Object.keys(e).length) nextErrors[getId(r)] = e;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [getId, isValidPLAccount, normalizeAccount, toNRBDigits26]);

  const downloadBlob = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onGenerateTransfers = () => {
    forceBlurActive();
    flushSave(listRef.current || []);
    if (!validateAll()) return;

    const rows = (listRef.current || []).slice(0, 50);
    const payload = rows.map((r) => ({
      trxId: r.TrxId,
      refundAmount: r.RefundAmount,
      creditorAccount: normalizeAccount(r.CreditorAccount),
      title: r.Title,
    }));

    fetchMssqlApi(
      "refund-elixir0",
      { method: "POST", data: payload, responseType: "blob", hideNotification: true },
      (blob) => downloadBlob(`Zwroty_elixir0_${moment().format("YYYY_MM_DD_HH_mm_ss")}.csv`, blob),
      (err) => {
        console.error(err);
        alert("Nie udało się wygenerować pliku ELIXIR-0.");
      }
    );
  };

  const onGeneratePdf = () => {
    forceBlurActive();
    flushSave(listRef.current || []);
    if (!validateAll()) return;

    const rows = listRef.current || [];
    const payload = rows.map((r) => ({
      trxId: r.TrxId,
      refundAmount: r.RefundAmount,
      creditorAccount: normalizeAccount(r.CreditorAccount),
      title: r.Title,
      createDateTime: r.CreateDateTime,
      machine: r.MachineFullName,
      serialNo: r.SerialNo,
      productNo: r.ProductNo,
      originalPrice: r.Price,
      terminalTypeName: r.TerminalTypeName,
      trx: r,
    }));

    fetchMssqlApi(
      "refund-pdf",
      { method: "POST", data: payload, responseType: "blob", hideNotification: true },
      (blob) => downloadBlob(`Zwroty_raport_zbiorczy_${moment().format("YYYY_MM_DD_HH_mm_ss")}.pdf`, blob),
      (err) => {
        console.error(err);
        alert("Nie udało się wygenerować PDF.");
      }
    );
  };

  const totalRefund = useMemo(() => {
    const rows = list || [];
    let sum = 0;
    for (let i = 0; i < rows.length; i++) sum += Number.parseFloat(rows[i].RefundAmount) || 0;
    return sum;
  }, [list]);

  const anyList = (list || []).length > 0;

  return (
    <>
      <Dropdown.Item
        as="button"
        className={anyList ? "bg-light mb-1" : "bg-secondary text-white mb-1"}
        onClick={() => anyList && setShow(true)}
        disabled={!anyList}
      >
        <i className="fas fa-undo" /> Obsługa reklamacji
      </Dropdown.Item>

      <Modal show={show} onHide={onClose} size="xl" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Zwrot środków</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {anyList && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted">
                  Pozycje: <b>{list.length}</b> &nbsp;|&nbsp; Suma zwrotów:{" "}
                  <b>{totalRefund.toFixed(2)} zł</b>
                </div>
              </div>

              <div style={{ maxHeight: "65vh", overflow: "auto" }}>
                <Table bordered hover size="sm" className="mb-0">
                  <thead className="sticky-top bg-light" style={{ top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: 260 }}>Transakcja</th>
                      <th style={{ width: 120 }}>Kwota zwrotu</th>
                      <th style={{ width: 260 }}>Konto odbiorcy</th>
                      <th style={{ width: 300 }}>Tytuł przelewu</th>
                      <th style={{ width: 50 }} className="text-center">
                        Usuń
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {(list || []).map((r) => {
                      const id = getId(r);
                      return (
                        <RefundRow
                          key={id}
                          r={r}
                          e={errors[id]}
                          onCommitRow={setRow}
                          onRemove={removeOne}
                          formatAccountLive={formatAccountLive}
                          getAccountLiveError={getAccountLiveError}
                        />
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button variant="outline-danger" onClick={clearAll}>
              Wyczyść
            </Button>
          </div>

          <div className="d-flex">
            <Button variant="outline-success" className="mr-2" onClick={onGenerateTransfers}>
              Wygeneruj przelewy (ELIXIR-0)
            </Button>

            <Button variant="outline-info" className="mr-2" onClick={onGeneratePdf}>
              Wygeneruj raport zbiorczy
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}